import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { ScrollButton } from '../components/SmartLink';
import { 
  Heart, 
  Users, 
  Award, 
  Briefcase,
  Clock,
  TrendingUp,
  Shield,
  Sparkles,
  ArrowRight,
  Check,
  MapPin
} from 'lucide-react';

const benefits = [
  {
    icon: Heart,
    title: 'Meaningful Work',
    description: 'Make a real difference in people\'s lives every single day.',
  },
  {
    icon: Clock,
    title: 'Flexible Schedules',
    description: 'Choose hours that fit your life with various shift options.',
  },
  {
    icon: TrendingUp,
    title: 'Career Growth',
    description: 'Ongoing training and advancement opportunities.',
  },
  {
    icon: Shield,
    title: 'Supportive Team',
    description: '24/7 support and a caring, collaborative environment.',
  },
  {
    icon: Award,
    title: 'Competitive Pay',
    description: 'Fair compensation that recognizes your valuable work.',
  },
  {
    icon: Sparkles,
    title: 'Recognition',
    description: 'We celebrate and appreciate our caregivers\' contributions.',
  },
];

const positions = [
  {
    title: 'Caregiver / Home Health Aide',
    type: 'Full-time / Part-time',
    description: 'Provide compassionate personal care, companionship, and daily living assistance to clients in their homes.',
    requirements: [
      'Previous caregiving experience preferred',
      'Compassionate and patient demeanor',
      'Reliable transportation',
      'Pass background check',
    ],
  },
  {
    title: 'Companion Care Specialist',
    type: 'Part-time / PRN',
    description: 'Offer meaningful companionship, social engagement, and light assistance to enhance clients\' quality of life.',
    requirements: [
      'Excellent communication skills',
      'Genuine interest in seniors\' well-being',
      'Flexible availability',
      'Pass background check',
    ],
  },
  {
    title: 'Live-In Caregiver',
    type: 'Full-time',
    description: 'Provide around-the-clock care and support for clients requiring continuous assistance in their homes.',
    requirements: [
      '2+ years caregiving experience',
      'Ability to handle varied care needs',
      'Strong problem-solving skills',
      'Pass background check',
    ],
  },
];

const values = [
  'Treating every client like family',
  'Maintaining the highest standards of care',
  'Supporting each other as a team',
  'Growing professionally and personally',
  'Making a positive impact daily',
];

export const CareersPage = () => {
  return (
    <>
      <Helmet>
        <title>Careers at Heritage Home Health LLC | Join Our Team</title>
        <meta
          name="description"
          content="Join the Heritage Home Health LLC team. We're looking for compassionate caregivers who want to make a difference. Competitive pay, flexible schedules, and growth opportunities."
        />
      </Helmet>
      <div className="min-h-screen bg-warm-beige" data-testid="careers-page">
        <Navigation />
        <main>
          {/* Hero Section */}
          <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-muted-green/10 via-transparent to-transparent" />
            <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-soft-gold/15 to-transparent rounded-full blur-3xl" />
            
            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
                    Join Our Team
                  </span>
                  <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy mb-6">
                    Careers at{' '}
                    <span className="text-gradient-gold">Heritage Home Health LLC</span>
                  </h1>
                  <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
                    Are you passionate about helping others? Join our team of dedicated 
                    caregivers who make a meaningful difference in the lives of individuals 
                    and families every day.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <ScrollButton
                      targetId="positions"
                      data-testid="careers-view-positions-btn"
                      className="group inline-flex items-center justify-center space-x-2 bg-navy text-white px-8 py-4 rounded-full font-semibold hover:bg-deep-blue transition-all shadow-soft-3d hover:shadow-soft-3d-lg hover:-translate-y-1"
                    >
                      <Briefcase className="w-5 h-5" />
                      <span>View Open Positions</span>
                    </ScrollButton>
                    <Link
                      to="/contact"
                      className="inline-flex items-center justify-center space-x-2 bg-white text-navy px-8 py-4 rounded-full font-semibold hover:bg-soft-gray transition-all border border-soft-gray shadow-soft-3d"
                    >
                      <span>Contact Us</span>
                    </Link>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="relative"
                >
                  <div className="rounded-3xl overflow-hidden shadow-soft-3d-lg">
                    <img
                      src="https://images.unsplash.com/photo-1613394242132-1268854bde44?crop=entropy&cs=srgb&fm=jpg&w=600&h=500&fit=crop"
                      alt="Compassionate caregiver"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                  <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                    className="absolute -bottom-6 -left-6 glass shadow-soft-3d rounded-2xl p-5 animate-float"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-muted-green/20 flex items-center justify-center">
                        <Users className="w-6 h-6 text-muted-green" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-navy">200+</p>
                        <p className="text-sm text-gray-500">Team Members</p>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="py-16 md:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
                  Why Join Us
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-navy mb-6">
                  Benefits of Working at{' '}
                  <span className="text-gradient-gold">Heritage Home Health LLC</span>
                </h2>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <motion.div
                      key={benefit.title}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="group p-8 rounded-3xl bg-warm-beige hover:bg-white hover:shadow-soft-3d-lg transition-all duration-500 hover:-translate-y-2"
                      data-testid={`benefit-card-${index}`}
                    >
                      <div className="w-14 h-14 rounded-2xl bg-soft-gold/20 flex items-center justify-center mb-6 group-hover:bg-soft-gold/30 transition-colors">
                        <Icon className="w-7 h-7 text-soft-gold" />
                      </div>
                      <h3 className="font-serif text-xl text-navy mb-2 group-hover:text-soft-gold transition-colors">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600">
                        {benefit.description}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Culture */}
          <section className="py-16 md:py-24 bg-warm-beige">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
                    Our Culture
                  </span>
                  <h2 className="font-serif text-3xl md:text-4xl text-navy mb-6">
                    What It Means to Be Part of{' '}
                    <span className="text-gradient-gold">Heritage Home Health LLC</span>
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed mb-8">
                    At Heritage Home Health LLC, we're more than colleagues—we're a family 
                    united by a shared mission to provide exceptional care. Our culture is 
                    built on mutual respect, continuous learning, and a genuine passion for 
                    making a difference.
                  </p>
                  <ul className="space-y-4">
                    {values.map((value, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center space-x-3"
                      >
                        <div className="w-6 h-6 rounded-full bg-muted-green/20 flex items-center justify-center">
                          <Check className="w-4 h-4 text-muted-green" />
                        </div>
                        <span className="text-gray-700">{value}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="rounded-3xl overflow-hidden shadow-soft-3d-lg">
                    <img
                      src="https://images.unsplash.com/photo-1646663133945-db03f563076a?crop=entropy&cs=srgb&fm=jpg&w=600&h=450&fit=crop"
                      alt="Team connection"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Open Positions */}
          <section id="positions" className="py-16 md:py-24 bg-white scroll-mt-32">
            <div className="max-w-5xl mx-auto px-6 md:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
                  Open Positions
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-navy mb-6">
                  Current <span className="text-gradient-gold">Opportunities</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Explore our available positions and find the right fit for your skills and passion.
                </p>
              </motion.div>

              <div className="space-y-6">
                {positions.map((position, index) => (
                  <motion.div
                    key={position.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="glass-strong shadow-soft-3d rounded-3xl p-8 hover:shadow-soft-3d-lg transition-all duration-300"
                    data-testid={`position-card-${index}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h3 className="font-serif text-2xl text-navy">{position.title}</h3>
                          <span className="px-3 py-1 bg-soft-gold/20 text-soft-gold text-sm font-medium rounded-full">
                            {position.type}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-4">{position.description}</p>
                        <div className="flex flex-wrap gap-2">
                          {position.requirements.map((req, i) => (
                            <span
                              key={i}
                              className="px-3 py-1 bg-warm-beige text-gray-700 text-sm rounded-full"
                            >
                              {req}
                            </span>
                          ))}
                        </div>
                      </div>
                      <Link
                        to="/contact"
                        className="group inline-flex items-center space-x-2 bg-navy text-white px-6 py-3 rounded-full font-semibold hover:bg-deep-blue transition-all shrink-0"
                      >
                        <span>Apply Now</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-12 text-center"
              >
                <p className="text-gray-600 mb-4">
                  Don't see a position that fits? We're always looking for talented individuals.
                </p>
                <Link
                  to="/contact"
                  data-testid="careers-general-inquiry"
                  className="group inline-flex items-center space-x-2 text-navy font-semibold hover:text-soft-gold transition-colors"
                >
                  <span>Send a General Inquiry</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </div>
          </section>

          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default CareersPage;
